import _get from 'lodash/get';
import {
  Task,
  TaskAssignationsInputObject,
  TaskBulkCreateSameInputObject,
  TaskKindEnum,
  TaskPriorityEnum,
  TaskUpdateInputObject,
  RecurringRuleInputObject,
} from 'graphql-common';
import { Option, Values } from '@lib/interfaces/form';
import mergeAndUnlinkMultiSelectValues from '@lib/utils/mergeAndUnlinkMultiSelectValues';
import { AssignationsType, FieldNames } from '../enums';
import getEarlyCompletionFields from './getEarlyCompletionFields';
import getRecurringRule from './getRecurringRule';

export default function getPreparedTaskDataFromValues(
  values: Values,
  prevData: Task | undefined,
  prevDocumentationLinks?: Option[],
  omitDocumentationLinks?: boolean,
): TaskBulkCreateSameInputObject | TaskUpdateInputObject {
  const isEdit = !!prevData?.id;
  let canQrCodeRelatedInfoUpdate = true;
  if (isEdit) {
    canQrCodeRelatedInfoUpdate =
      _get(prevData, 'canQrCodeRelatedInfoUpdate.value') || false;
  }

  const getApproverId = () =>
    _get(values, `${FieldNames.approver}.value`) as string;
  const name = _get(values, FieldNames.taskName) as string;
  const description = _get(values, FieldNames.taskDescription) as string;
  const priority = _get(
    values,
    `${FieldNames.priority}.value`,
  ) as TaskPriorityEnum;
  const templateId = _get(values, `${FieldNames.template}.value`) as string;
  const templateVersionId = _get(
    values,
    `${FieldNames.templateVersion}.value`,
  ) as string;

  const documentationLinksValues = (_get(
    values,
    FieldNames.documentationLinks,
  ) || []) as Option[];
  const newItems = documentationLinksValues.map(({ value }) => ({
    id: `${value}`,
  }));
  const prevItems = (prevDocumentationLinks || []).map(({ value }) => ({
    id: `${value}`,
  }));
  const documentationLinks = omitDocumentationLinks
    ? undefined
    : mergeAndUnlinkMultiSelectValues(prevItems, newItems);

  let assignationsType;
  let assignationsValue;
  let assetId;
  let siteAreaId;
  if (isEdit) {
    const editAssignationsType = _get(
      values,
      [FieldNames.assignationsType, 'value'],
      AssignationsType.Area,
    ) as AssignationsType;
    assetId =
      editAssignationsType === AssignationsType.Equipment
        ? _get(values, [FieldNames.asset, 'value'], null)
        : null;
    siteAreaId =
      editAssignationsType === AssignationsType.Area
        ? _get(values, [FieldNames.siteArea, 'value'], null)
        : null;
  } else {
    assignationsType = _get(
      values,
      [FieldNames.assignationsType, 'value'],
      AssignationsType.Area,
    ) as AssignationsType;
    const siteAreaAssignations = _get(
      values,
      FieldNames.siteArea,
      [],
    ) as Option[];
    const assetAssignations = _get(values, FieldNames.asset, []) as Option[];
    const assignations: TaskAssignationsInputObject = {};
    if (assignationsType === AssignationsType.Area) {
      assignations.siteAreaIds = siteAreaAssignations.map(
        ({ value }) => `${value}`,
      );
    } else if (assignationsType === AssignationsType.Equipment) {
      assignations.assetIds = assetAssignations.map(({ value }) => `${value}`);
    }
    assignationsValue = assignations;
  }

  const taskData = {
    approverId: getApproverId(),
    assetId: canQrCodeRelatedInfoUpdate ? assetId : undefined,
    assignations: assignationsValue,
    description,
    documentationLinks,
    name,
    priority,
    siteAreaId: canQrCodeRelatedInfoUpdate ? siteAreaId : undefined,
    templateId: canQrCodeRelatedInfoUpdate ? templateId : undefined,
    templateVersionId,
  };

  if (prevData?.id) {
    return taskData;
  }

  const kind = _get(
    values,
    `${FieldNames.taskKind}.value`,
    TaskKindEnum.Standard,
  ) as TaskKindEnum;

  let deadlineAt: string | undefined;
  if (_get(values, FieldNames.deadlineAt) && kind === TaskKindEnum.Standard) {
    deadlineAt = _get(values, FieldNames.deadlineAt) as string;
  }

  const standrtData = {
    deadlineAt,
    ...getEarlyCompletionFields(values),
    recurringRule: getRecurringRule(values) as RecurringRuleInputObject,
  };

  return {
    // Step 1
    ...taskData,
    // Step 2
    kind,
    ...(kind === TaskKindEnum.Standard ? standrtData : {}),
  };
}
